import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchTAEDSnapshotByReferenceNumber, getTAEDEventsByReferenceNumber } from "../../api/taed";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice";
import { STATUS_CANCELED, WEEKDAYS_SHORT } from "../../util/constants";
import { format_DOW_ShortMonth_Date_Year } from "../../util/dateTimeFormatter";
import { TEMP_ASSIGNMENT_BASE_SCHEDULE_MISSING_MESSAGE } from "../../util/messageConstants";
import { getScrubbedTempAssignmentSnapshot } from "../../util/taed/tempAssignmentScrubber";
import { isBaseScheduleAdded } from "../../util/taedUtils";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsCloseIconButton from "../fundamentals/StarsCloseIconButton.jsx";
import TemporaryAssignmentAuditHistoryDialog from "./TemporaryAssignmentAuditHistoryDialog";

const useStyles = makeStyles((theme) => ({
  baseScheduleMissingChip: {
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.swaRed,
    color: theme.palette.secondary.swaWhite,
    width: "80px",
  },
}));

const TemporaryAssignmentDetailDialog = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isTAEDDetailDialogLoading, setIsTAEDDetailDialogLoading] = useState(true);
  const [isAuditHistoryLoading, setIsAuditHistoryLoading] = useState(false);
  const [tempAssignment, setTempAssignment] = useState("");
  const [tempAssignmentEvents, setTempAssignmentEvents] = useState("");

  useEffect(() => {
    fetchTAEDSnapshotByReferenceNumber(props.tempAssignmentReferenceNumber, userSession.isAdmin, stationFilter)
      .then((result) => {
        if (result.message) {
          setErrorMessage(result.message);
        } else {
          let tempAssignmentSnapshot = result.taedSnapshot;
          let scrubbedTempAssignmentSnapshot = getScrubbedTempAssignmentSnapshot(tempAssignmentSnapshot, stationFilter);
          setTempAssignment(scrubbedTempAssignmentSnapshot);
        }
        setIsTAEDDetailDialogLoading(false);
      })
      .catch((err) => {
        console.error("error happened!", err);
        setErrorMessage("Unexpected error occurred.");
        setIsTAEDDetailDialogLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    props.handleClickDialogClose(event, reason);
  };
  const handleAuditHistory = () => {
    setIsAuditHistoryLoading(true);
    getAuditHistory(tempAssignment.referenceNumber);
  };

  const getAuditHistory = async (referenceNumber) => {
    await getTAEDEventsByReferenceNumber(referenceNumber)
      .then((result) => {
        setTempAssignmentEvents(result.tempAssignmentEvents);
        setIsAuditHistoryLoading(false);
        setDialogOpen(true);
      })
      .catch((err) => {
        console.log(err, "error");
        setIsAuditHistoryLoading(false);
      });
  };

  const renderBaseSchedule = () => {
    return WEEKDAYS_SHORT.map((day, idx) => {
      return (
        <>
          <Grid item xs={6}>
            <div>{day}</div>
          </Grid>
          <Grid item xs={6}>
            {tempAssignment.baseSchedule[day].isOFF ? (
              <div className="detail-dialog-right-text">OFF</div>
            ) : tempAssignment.baseSchedule[day].isNA ? (
              <div className="detail-dialog-right-text">N/A</div>
            ) : (
              <div className="detail-dialog-right-text">
                {tempAssignment.baseSchedule[day].start.hr}:{tempAssignment.baseSchedule[day].start.min}-
                {tempAssignment.baseSchedule[day].end.hr}:{tempAssignment.baseSchedule[day].end.min}
              </div>
            )}
          </Grid>
          {idx !== 6 ? (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          ) : (
            ""
          )}
        </>
      );
    });
  };

  return (
    <Dialog
      className="detail-dialog"
      data-testid="temporary-assignment-information-dialog"
      fullWidth
      maxWidth="md"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleClickDialogClose}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        <StarsCloseIconButton onClick={handleClickDialogClose} />
      </DialogTitle>
      {isTAEDDetailDialogLoading ? (
        <Box
          sx={{
            margin: "auto",
            height: "50vh",
            width: "50vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : errorMessage !== "" ? (
        <Alert sx={{ width: "40vw", margin: "50px auto" }} severity="error">
          {errorMessage}
        </Alert>
      ) : (
        <>
          <DialogContent className="detail-dialog-content">
            <DialogContentText component={"div"} id="alert-dialog-slide-description">
              <Grid container>
                <Grid item xs={6}>
                  <Paper className="detail-dialog-section">
                    <Typography component={"div"}>
                      <Box className="detail-dialog-section-title">Temporary Assignment Information</Box>
                    </Typography>
                    <Grid container>
                      <Grid item xs={6}>
                        <div>Reference #</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.referenceNumber}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Latest Status</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.status}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Temporary Station</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.tempStation}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Created Date</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">
                          {format_DOW_ShortMonth_Date_Year(
                            getStationTimeZoneDateTimeStringFromUTCInteger(
                              tempAssignment.createdDate,
                              tempAssignment.tempStation
                            )
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Assignment Start Date</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">
                          {format_DOW_ShortMonth_Date_Year(tempAssignment.startDate)}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Assignment End Date</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">
                          {format_DOW_ShortMonth_Date_Year(tempAssignment.endDate)}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      {tempAssignment.effectiveStartDate ? (
                        <>
                          <Grid item xs={5}>
                            <div>Effective Start Date</div>
                          </Grid>
                          <Grid item xs={7}>
                            <div className="detail-dialog-right-text">
                              {getStationTimeZoneDateTimeStringFromUTCInteger(
                                tempAssignment.effectiveStartDate,
                                tempAssignment.tempStation
                              ).toFormat("ccc LLL dd, yyyy T ZZZZ")}
                            </div>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        </>
                      ) : (
                        ""
                      )}
                      {tempAssignment.effectiveEndDate ? (
                        <>
                          <Grid item xs={5}>
                            <div>Effective End Date</div>
                          </Grid>
                          <Grid item xs={7}>
                            <div className="detail-dialog-right-text">
                              {getStationTimeZoneDateTimeStringFromUTCInteger(
                                tempAssignment.effectiveEndDate,
                                tempAssignment.tempStation
                              ).toFormat("ccc LLL dd, yyyy T ZZZZ")}
                            </div>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        </>
                      ) : (
                        ""
                      )}
                    </Grid>
                  </Paper>
                  <Paper className="detail-dialog-section">
                    <Typography component={"div"}>
                      <Box className="detail-dialog-sub-section-title">Employee</Box>
                    </Typography>
                    <Grid container>
                      <Grid item xs={6}>
                        <div>Name</div>
                      </Grid>
                      <Grid item xs={6}>
                        {" "}
                        <div className="detail-dialog-right-text">
                          {tempAssignment.empLastName}, {tempAssignment.empFirstName}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>ID</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.eid}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Home Station</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.homeStation}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Workgroup</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.workgroup}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>FT/PT</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.ftPt}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Contact #</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{tempAssignment.contactNumber}</div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="detail-dialog-section">
                    <Typography component={"div"}>
                      <Grid container justifyContent="space-between">
                        <Box className="detail-dialog-sub-section-title">Base Schedule</Box>
                        {!isBaseScheduleAdded(tempAssignment.baseSchedule) && (
                          <Chip
                            className={classes.baseScheduleMissingChip}
                            label={TEMP_ASSIGNMENT_BASE_SCHEDULE_MISSING_MESSAGE}
                          />
                        )}
                      </Grid>
                    </Typography>
                    <Grid container>{isBaseScheduleAdded(tempAssignment.baseSchedule) && renderBaseSchedule()}</Grid>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Grid container justifyContent="flex-end" mr={2} mb={1}>
              {stationFilter !== tempAssignment.homeStation && tempAssignment.status !== STATUS_CANCELED ? (
                <Grid item xs={1}>
                  <Button variant="contained" onClick={props.handleEditClick}>
                    Edit
                  </Button>
                </Grid>
              ) : (
                ""
              )}
              <Grid item xs={2}>
                <Button
                  sx={{ width: "145px" }}
                  variant="contained"
                  onClick={handleAuditHistory}
                  disabled={isAuditHistoryLoading}
                >
                  {isAuditHistoryLoading ? <CircularProgress size={24} /> : "Audit History"}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </>
      )}

      {isDialogOpen && (
        <TemporaryAssignmentAuditHistoryDialog
          isDialogOpen={isDialogOpen}
          setDialogOpen={setDialogOpen}
          tempAssignmentEvents={tempAssignmentEvents}
        />
      )}
    </Dialog>
  );
};

TemporaryAssignmentDetailDialog.propTypes = {
  tempAssignmentReferenceNumber: PropTypes.string.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  handleClickDialogClose: PropTypes.func.isRequired,
  handleEditClick: PropTypes.func.isRequired,
};

export default TemporaryAssignmentDetailDialog;
